import { buildAjv } from "./jsonSchema";
import { isInputRecord } from "./priceModelInputMetadataPaths";
import { PRICE_MODEL_ALLOWED_PROPERTY_TYPES } from "./schemas/priceModels";

/**
 * Validates a price model's input schema, returning the reason it is unusable
 * or null when it is fine. A schema is usable when it compiles under the app's
 * JSON Schema vocabulary and limits its properties to primitives or one-level
 * arrays. Declaring no properties simply means the model takes no inputs.
 */
export function validatePriceModelInputSchema(
  inputSchema: Record<string, unknown>,
): string | null {
  const compileError = getCompileError(inputSchema);
  if (compileError) {
    return `Input schema is not a valid JSON Schema: ${compileError}`;
  }

  // Compiling already rejects a non-object `properties`, so anything left that
  // is not a record is an absent one.
  const properties = inputSchema.properties;
  if (!isInputRecord(properties)) {
    return null;
  }

  const invalidFields = Object.entries(properties).flatMap(
    ([key, propSchema]) => validateProperty(key, propSchema),
  );
  if (invalidFields.length === 0) {
    return null;
  }
  return `Input schema properties must be primitive types or arrays of primitives/flat objects. Invalid fields: ${invalidFields.join(", ")}`;
}

/**
 * Extracts per-property `default` values from a JSON Schema's top-level properties.
 * @param inputSchema - A JSON Schema object with a `properties` key
 * @returns Object mapping property names to their schema-defined default values
 */
export function extractSchemaDefaults(
  inputSchema: Record<string, unknown>,
): Record<string, unknown> {
  const properties = inputSchema.properties;
  if (!properties || typeof properties !== "object") {
    return {};
  }

  const defaults: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries(
    properties as Record<string, Record<string, unknown>>,
  )) {
    if ("default" in propSchema) {
      defaults[key] = propSchema.default;
    }
  }
  return defaults;
}

/**
 * Compiles a schema to surface the errors Ajv only raises at compile time, such
 * as an unknown keyword left by values saved in place of a schema. Uses its own
 * instance, since Ajv rejects a second schema reusing an already-compiled `$id`.
 */
function getCompileError(inputSchema: Record<string, unknown>): string | null {
  try {
    buildAjv({}).compile(inputSchema);
    return null;
  } catch (error) {
    return error instanceof Error ? error.message : String(error);
  }
}

/** Validates a single top-level property, returning one message per problem. */
function validateProperty(fieldName: string, propSchema: unknown): string[] {
  if (!isInputRecord(propSchema)) {
    return [`"${fieldName}" is not a schema object`];
  }

  const propType = propSchema.type;
  if (
    typeof propType !== "string" ||
    !PRICE_MODEL_ALLOWED_PROPERTY_TYPES.has(propType)
  ) {
    return [`"${fieldName}" has unsupported type "${String(propType)}"`];
  }

  return propType === "array"
    ? validateArrayProperty(fieldName, propSchema)
    : [];
}

/**
 * Validates the items sub-schema of an array property. Array items must be a
 * primitive type or a flat object whose own properties are all primitives.
 */
function validateArrayProperty(
  fieldName: string,
  propSchema: Record<string, unknown>,
): string[] {
  const items = propSchema.items;
  if (!isInputRecord(items)) {
    return [`"${fieldName}" is an array but has no valid "items" schema`];
  }

  const itemType = items.type;
  if (typeof itemType !== "string") {
    return [`"${fieldName}.items" has no valid type`];
  }

  if (
    PRICE_MODEL_ALLOWED_PROPERTY_TYPES.has(itemType) &&
    itemType !== "array"
  ) {
    return [];
  }

  if (itemType !== "object") {
    return [`"${fieldName}.items" has unsupported type "${itemType}"`];
  }

  const subProperties = items.properties;
  if (!isInputRecord(subProperties)) {
    return [];
  }

  return Object.entries(subProperties).flatMap(([subKey, subPropSchema]) => {
    const subType = isInputRecord(subPropSchema)
      ? subPropSchema.type
      : undefined;
    if (
      typeof subType !== "string" ||
      !PRICE_MODEL_ALLOWED_PROPERTY_TYPES.has(subType) ||
      subType === "array"
    ) {
      return [
        `"${fieldName}.items.${subKey}" has unsupported type "${String(subType)}" (must be a primitive)`,
      ];
    }
    return [];
  });
}
